import React, { useRef, useState } from "react";
import api, { errMsg, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, Loader2, Camera } from "lucide-react";

const ALLOWED = ["image/jpeg", "image/png", "image/webp", "image/gif"];

export function ImageUpload({ value, onChange, purpose = "general", testid = "image-upload", round = false, hint }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) { toast.error("Please choose a JPG, PNG, WEBP or GIF image"); e.target.value = ""; return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large. Maximum size is 8 MB."); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?purpose=${purpose}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.file_id);
      toast.success("Image uploaded.");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const src = value ? fileUrl(value) : null;

  return (
    <div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} data-testid={`${testid}-input`} />
      {src ? (
        <div className={`relative inline-block ${round ? "" : "w-full"}`}>
          <img src={src} alt="Uploaded" className={round ? "w-24 h-24 rounded-full object-cover border border-slate-200" : "w-full max-h-52 object-cover rounded-xl border border-slate-200"} data-testid={`${testid}-preview`} />
          <button type="button" onClick={() => onChange(null)} data-testid={`${testid}-remove`} className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-rose-500 text-white flex items-center justify-center shadow">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testid}-btn`}
          className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-xl text-slate-400 hover:border-primary hover:text-primary transition-colors ${round ? "w-24 h-24 rounded-full" : "w-full py-8"}`}>
          {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : (round ? <Camera className="w-6 h-6" /> : <Upload className="w-6 h-6" />)}
          {!round && <span className="text-xs font-medium">{uploading ? "Uploading…" : "Click to upload an image"}</span>}
        </button>
      )}
      {hint && !src && <p className="text-xs text-slate-400 mt-1.5">{hint}</p>}
    </div>
  );
}
